import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import api from "../../api/client";

const COMPANY_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+",
];

const BENEFITS = [
  {
    icon: "🎯",
    title: "Find the right candidates",
    desc: "rowse candidate profiles filtered by skills, availability, and location. Candidates opt in and control their own visibility — no names, no gender, no photos shown on Joborigo.",
  },
  {
    icon: "📨",
    title: "Send job positions directly",
    desc: "Reach candidates who have opted in. They receive your position in their inbox and can apply with one click.",
  },
  {
    icon: "🔗",
    title: "Integrate your ATS",
    desc: "Push status updates directly to candidates via our API. No more ghosting. Better employer brand.",
  },
  {
    icon: "⚖️",
    title: "EU Pay Transparency ready",
    desc: "Built for the EU Pay Transparency Directive (2026). Salary disclosure tools included from day one.",
  },
];

function getPasswordStrength(password) {
  if (!password) return null;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  if (passed <= 1)
    return {
      label: "Weak",
      color: "bg-red-400",
      width: "w-1/4",
      textColor: "text-red-500",
    };
  if (passed === 2)
    return {
      label: "Fair",
      color: "bg-amber-400",
      width: "w-2/4",
      textColor: "text-amber-500",
    };
  if (passed === 3)
    return {
      label: "Good",
      color: "bg-blue-400",
      width: "w-3/4",
      textColor: "text-blue-500",
    };
  return {
    label: "Strong",
    color: "bg-green-400",
    width: "w-full",
    textColor: "text-green-500",
  };
}

function validatePassword(password) {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(password))
    return "Password must contain at least one uppercase letter";
  if (!/\d/.test(password)) return "Password must contain at least one number";
  return null;
}

export default function EmployerJoin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const slug = searchParams.get("slug");
  const setEmployerAuth = useAuthStore((s) => s.setEmployerAuth);

  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({
    company_name: "",
    company_website: "",
    company_size: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Employer Portal";
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      const pwError = validatePassword(form.password);
      if (pwError) {
        setError(pwError);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const endpoint =
        mode === "register"
          ? "/employer/auth/register"
          : "/employer/auth/login";
      const payload =
        mode === "register"
          ? form
          : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload, {
        withCredentials: true,
      });
      setEmployerAuth(data.access_token, data.employer);
      navigate(slug ? `/r/${slug}` : "/employers/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(form.password);
  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
        <Logo className="h-12" />
        <div className="flex items-center gap-3">
          <ThemeToggle />
          {slug && (
            <Link
              to={`/r/${slug}`}
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
            >
              Back to resume
            </Link>
          )}
        </div>
      </nav>

      <main className="flex-1 flex flex-col lg:flex-row">
        <div
          className="flex-1 relative px-8 py-12 flex flex-col justify-center overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #4B1FA8 0%, #6B3FD4 50%, #9B2C8A 100%)",
          }}
        >
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          <div className="max-w-md mx-auto lg:mx-0 relative z-10">
            {from === "resume" && slug && (
              <div className="mb-8 bg-white/15 border border-white/20 rounded-2xl px-5 py-4 backdrop-blur-sm">
                <p className="text-white/70 text-xs mb-1 uppercase tracking-widest font-medium">
                  You came from a Joborigo resume
                </p>
                <p className="text-white text-sm leading-relaxed">
                  Create a free employer account to send this candidate a job
                  position. They will see it in their Joborigo inbox instantly.
                </p>
              </div>
            )}
            <h1 className="text-3xl font-bold text-white mb-2 leading-tight">
              Find and hire better candidates
            </h1>
            <p className="text-white/70 mb-10 leading-relaxed">
              Joborigo connects you with talented professionals who are ready to
              talk. Skill-first. Bias-free. EU-compliant.
            </p>
            <div className="flex flex-col gap-5">
              {BENEFITS.map((b) => (
                <div key={b.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-xl flex-shrink-0">
                    {b.icon}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-0.5">
                      {b.title}
                    </p>
                    <p className="text-white/60 text-xs leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-white/20">
              <p className="text-white/50 text-xs">
                Free to create an account. No credit card required.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-brand-light-bg dark:bg-brand-dark-bg">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8">
              <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest">
                Employer portal
              </span>
              <div className="flex gap-2 mt-3 mb-6">
                {["register", "login"].map((m) => (
                  <button
                    key={m}
                    onClick={() => {
                      setMode(m);
                      setError(null);
                    }}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${mode === m ? "bg-brand-violet text-white" : "bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"}`}
                  >
                    {m === "register" ? "Create account" : "Sign in"}
                  </button>
                ))}
              </div>

              {error && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {error}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {mode === "register" && (
                  <>
                    <div>
                      <label className={labelClass}>Company name *</label>
                      <input
                        name="company_name"
                        required
                        value={form.company_name}
                        onChange={handleChange}
                        placeholder="Acme Corp"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Company website</label>
                      <input
                        name="company_website"
                        type="url"
                        value={form.company_website}
                        onChange={handleChange}
                        placeholder="https://acme.com"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Company size</label>
                      <select
                        name="company_size"
                        value={form.company_size}
                        onChange={handleChange}
                        className={inputClass}
                      >
                        <option value="">Select size...</option>
                        {COMPANY_SIZES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className={labelClass}>Work email *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Password *</label>
                  <input
                    name="password"
                    type="password"
                    required
                    value={form.password}
                    onChange={handleChange}
                    placeholder={
                      mode === "register" ? "Min 8 characters" : "Your password"
                    }
                    className={inputClass}
                  />
                  {mode === "register" && strength && (
                    <div className="mt-2">
                      <div className="h-1 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <p
                          className={`text-xs font-medium ${strength.textColor}`}
                        >
                          {strength.label}
                        </p>
                        <p className="text-xs text-gray-400">
                          {!/[A-Z]/.test(form.password) && "Add uppercase "}
                          {!/\d/.test(form.password) && "Add number "}
                          {!/[^A-Za-z0-9]/.test(form.password) && "Add symbol"}
                        </p>
                      </div>
                    </div>
                  )}
                  {mode === "register" && (
                    <p className="text-xs text-gray-400 mt-1.5">
                      Min 8 characters, one uppercase letter and one number
                    </p>
                  )}
                </div>
                {mode === "register" && (
                  <p className="text-xs text-gray-400 leading-relaxed">
                    By registering you agree to our{" "}
                    <Link
                      to="/terms"
                      className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
                >
                  {loading
                    ? mode === "register"
                      ? "Creating account..."
                      : "Signing in..."
                    : mode === "register"
                      ? "Create employer account"
                      : "Sign in"}
                </button>
              </form>

              {slug && (
                <div className="mt-4 bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark">
                    After{" "}
                    {mode === "register"
                      ? "creating your account"
                      : "signing in"}{" "}
                    you will be taken back to the candidate resume.
                  </p>
                </div>
              )}
            </div>
            <p className="text-center text-sm text-gray-400 mt-4">
              Looking for the job seeker app?{" "}
              <Link
                to="/register"
                className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                Register as a seeker
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
